import { useRef, useState } from "react";
import { FiInfo, FiMinus, FiPlus, FiArrowRight, FiCreditCard, FiShield, FiClock } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSidebar } from "units/dashboard/context/SidebarProvider";
import useMetisMenu from "units/dashboard/hooks/useMetisMenu";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const ClientWallet = () => {
    const { t } = useTranslation(["client"]);

    const { username } = useParams();
    const dispatch = useDispatch();
    const sideMenuRef = useMetisMenu();
    const navigate = useNavigate();
    const location = useLocation();
    const [active, setActive] = useState(null);
    const [isChatMenuOpen, setIsChatMenuOpen] = useState(true);
    const [isWalletMenuOpen, setIsWalletMenuOpen] = useState(false);
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const sidebarRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [wallet, setWallet] = useState(0);
    const [error, setError] = useState(null);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    const options = [15000000, 10000000, 5000000, 3000000, 2000000, 1000000];

    // ✅ فقط برای پارس کردن ورودی (کاربر ممکنه فارسی تایپ کنه)
    const persianToEnglishDigits = (str) => {
        if (str === null || str === undefined) return "";
        return String(str)
            .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
            .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)); // عربی هم
    };

    // ✅ ایمن‌شده و همیشه رشته برمی‌گردونه
    const formatNumberWithCommas = (num) => {
        if (num === null || num === undefined || num === "") return "0";
        const n = Number(String(num).replace(/,/g, ""));
        if (isNaN(n)) return "0";
        return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const [amount, setAmount] = useState(formatNumberWithCommas(1000000));

    const handleSelect = (value) => {
        setSelectedOption(value);
        setAmount(formatNumberWithCommas(value));
        setError(null);
    };

    const handleDecrease = () => {
        const cleanAmount = parseInt(persianToEnglishDigits(amount.toString().replace(/,/g, '')), 10) || 0;
        const decreased = Math.max(cleanAmount - 1000000, 0);
        setAmount(formatNumberWithCommas(decreased));
        setSelectedOption(null);
        setError(null);
    };

    const handleIncrease = () => {
        const cleanAmount = parseInt(persianToEnglishDigits(amount.toString().replace(/,/g, '')), 10) || 0;
        const increased = cleanAmount + 100000;
        setAmount(formatNumberWithCommas(increased));
        setSelectedOption(null);
        setError(null);
    };

    const handleInputChange = (e) => {
        const rawValue = e.target.value;
        const english = persianToEnglishDigits(rawValue.replace(/,/g, ""));

        if (rawValue === "") {
            setAmount("");
            setError(null);
            setSelectedOption(null);
            return;
        }

        const num = parseInt(english, 10);

        if (isNaN(num)) {
            setAmount("");
            setError(t("wallet.invalidNumber"));
            return;
        }

        if (num < 100000) {
            setError(t("wallet.minimumAmount"));
        } else {
            setError(null);
        }

        setAmount(formatNumberWithCommas(num));
        setSelectedOption(null);
    };

    const payButtonHandler = () => {
        const cleanAmount = persianToEnglishDigits(amount.toString().replace(/,/g, ''));
        const numericAmount = parseInt(cleanAmount, 10);

        if (isNaN(numericAmount) || numericAmount < 100000) {
            setError(t("wallet.minimumChargeAmount"));
            return;
        }

        navigate(`/bot/payment-gateway-method/${username}`, { state: { finalPrice: numericAmount } });
    };

    const getNumericAmount = () => {
        const cleanAmount = persianToEnglishDigits(amount.toString().replace(/,/g, ''));
        return parseInt(cleanAmount, 10) || 0;
    };

    return (
        <>
            {loading ? (
                <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:!from-gray-900 dark:!to-gray-950">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-teal-600 dark:!border-teal-400"></div>
                </div>
            ) : (
                <div className="min-h-screen bg-gradient-to-br rounded-2xl from-gray-50 via-white to-teal-50/30 dark:!from-gray-950 dark:!via-gray-900 dark:!to-teal-950/40">
                    <div className="main-content flex-grow m-0 pb-12">
                        <div className="container-fluid px-4 md:px-6 pt-8">
                            {/* Header */}
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="mb-8"
                            >
                                <div className="flex flex-col gap-4">
                                    <div>
                                        <h4 className="text-2xl md:text-3xl font-bold text-gray-800 dark:!text-gray-100">
                                            {t("wallet.title")}
                                        </h4>
                                        <p className="text-gray-500 dark:!text-gray-400 text-sm mt-1">
                                            {t("wallet.subtitle")}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Main Card */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="max-w-2xl mx-auto"
                            >
                                <div className="bg-white dark:!bg-gray-900 rounded-2xl shadow-xl dark:!shadow-black/40 border border-gray-100 dark:!border-gray-800 overflow-hidden">
                                    {/* Balance Card */}
                                    <div className="bg-gradient-to-r from-teal-600 to-teal-700 dark:!from-teal-700 dark:!to-teal-900 p-6 text-white">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-1">
                                                <p className="text-teal-100 dark:!text-teal-200/80 text-sm flex items-center gap-1">
                                                    <FiInfo className="w-4 h-4" />
                                                    {t("wallet.currentBalance")}
                                                </p>
                                                <p className="text-3xl font-bold">
                                                    {formatNumberWithCommas(wallet)}
                                                    <span className="text-sm font-normal mr-1 text-teal-100 dark:!text-teal-200/80">
                                                        {t("wallet.currency")}
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="bg-white/10 dark:!bg-white/5 p-3 rounded-xl backdrop-blur-sm">
                                                <FiCreditCard className="w-6 h-6" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6">
                                        {/* Quick Amount Options */}
                                        <div className="mb-6">
                                            <p className="text-sm font-medium text-gray-700 dark:!text-gray-300 mb-3">
                                                {t("wallet.suggestedAmounts")}
                                            </p>
                                            <div className="grid grid-cols-3 gap-2">
                                                {options.map((opt) => {
                                                    const isSelected = selectedOption === opt ||
                                                        parseInt(persianToEnglishDigits(amount.toString().replace(/,/g, '')), 10) === opt;
                                                    return (
                                                        <motion.button
                                                            key={opt}
                                                            whileHover={{ scale: 1.02 }}
                                                            whileTap={{ scale: 0.98 }}
                                                            onClick={() => handleSelect(opt)}
                                                            className={`py-2.5 px-3 rounded-xl text-xm font-medium transition-all duration-200 ${isSelected
                                                                    ? "bg-teal-100 text-teal-700 border-2 border-teal-500 shadow-md dark:!bg-teal-900/40 dark:!text-teal-200 dark:!border-teal-500 dark:!shadow-teal-900/40"
                                                                    : "bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-teal-300 hover:bg-teal-50 dark:!bg-gray-800 dark:!text-gray-200 dark:!border-gray-700 dark:!hover:border-teal-600 dark:!hover:bg-teal-950/40"
                                                                }`}
                                                        >
                                                            {formatNumberWithCommas(opt)}
                                                            <span className="block text-[10px] opacity-60">
                                                                {t("wallet.currency")}
                                                            </span>
                                                        </motion.button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Amount Input */}
                                        <div className="mb-4">
                                            <label className="text-sm font-medium text-gray-700 dark:!text-gray-300 block mb-2">
                                                {t("wallet.customAmount")}
                                            </label>
                                            <div className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all duration-200 ${error
                                                    ? "border-red-400 bg-red-50 dark:!border-red-500/70 dark:!bg-red-950/40"
                                                    : isFocused
                                                        ? "border-teal-500 bg-teal-50 dark:!border-teal-500 dark:!bg-teal-950/40"
                                                        : "border-gray-200 bg-gray-50 dark:!border-gray-700 dark:!bg-gray-800"
                                                }`}>
                                                <motion.button
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    onClick={handleDecrease}
                                                    disabled={getNumericAmount() <= 0}
                                                    className={`p-2 rounded-lg transition-all ${getNumericAmount() <= 0
                                                            ? "text-gray-300 dark:!text-gray-600 cursor-not-allowed"
                                                            : "text-teal-600 dark:!text-teal-400 hover:bg-teal-100 dark:!hover:bg-teal-900/40"
                                                        }`}
                                                >
                                                    <FiMinus className="w-5 h-5" />
                                                </motion.button>

                                                <div className="relative flex-1 flex justify-center items-center">
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={amount}
                                                        onChange={handleInputChange}
                                                        onFocus={() => setIsFocused(true)}
                                                        onBlur={() => setIsFocused(false)}
                                                        className="w-full max-w-[200px] bg-transparent text-gray-800 dark:!text-gray-100 text-lg font-bold text-center outline-none placeholder:text-gray-400 dark:!placeholder:text-gray-500"
                                                        placeholder="0"
                                                    />
                                                    <span className="text-xs text-gray-500 dark:!text-gray-400 mr-1">
                                                        {t("wallet.currency")}
                                                    </span>
                                                </div>

                                                <motion.button
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    onClick={handleIncrease}
                                                    className="p-2 rounded-lg text-teal-600 dark:!text-teal-400 hover:bg-teal-100 dark:!hover:bg-teal-900/40 transition-all"
                                                >
                                                    <FiPlus className="w-5 h-5" />
                                                </motion.button>
                                            </div>
                                            {error && (
                                                <motion.p
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-red-500 dark:!text-red-400 text-xs mt-2 flex items-center gap-1"
                                                >
                                                    {error}
                                                </motion.p>
                                            )}
                                        </div>

                                        {/* Features */}
                                        <div className="grid grid-cols-3 gap-3 mb-6">
                                            {[
                                                { icon: FiShield, label: t("wallet.features.security") },
                                                { icon: FiClock, label: t("wallet.features.fastPayment") },
                                                { icon: FiCreditCard, label: t("wallet.features.support") }
                                            ].map((feature, index) => (
                                                <div key={index} className="flex flex-col items-center p-2 bg-gray-50 dark:!bg-gray-800 rounded-xl">
                                                    <feature.icon className="w-5 h-5 text-teal-600 dark:!text-teal-400 mb-1" />
                                                    <span className="text-xs text-gray-600 dark:!text-gray-400">{feature.label}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Submit Button */}
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={payButtonHandler}
                                            className={`w-full py-3.5 rounded-xl text-white font-bold text-base transition-all duration-200 flex items-center justify-center gap-2 ${getNumericAmount() >= 100000
                                                    ? "bg-gradient-to-r from-teal-600 to-teal-700 dark:!from-teal-600 dark:!to-teal-800 hover:shadow-lg hover:shadow-teal-500/30 dark:!hover:shadow-teal-500/20"
                                                    : "bg-gray-300 dark:!bg-gray-700 cursor-not-allowed"
                                                }`}
                                            disabled={getNumericAmount() < 100000}
                                        >
                                            {t("wallet.continuePayment")}
                                            <FiArrowRight className="w-5 h-5" />
                                        </motion.button>

                                        {/* Footer Note */}
                                        <div className="mt-4 text-center">
                                            <p className="text-xs text-gray-400 dark:!text-gray-500">
                                                {t("wallet.minimumChargeNote", {
                                                    amount: formatNumberWithCommas(100000)
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientWallet;